import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import ProductCard from "./ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imgSrc = (url) => {
    if (!url) return "/default-item.svg";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
};

// Lightweight cache (per page load) so we don't refetch /api/categories per rail
let _catCache = null;
let _catCachePromise = null;
const loadCats = () => {
    if (_catCache) return Promise.resolve(_catCache);
    if (!_catCachePromise) {
        _catCachePromise = axios.get(`${API}/categories?storefront=true`)
            .then((r) => { _catCache = r.data || []; return _catCache; })
            .catch(() => { _catCache = []; return _catCache; });
    }
    return _catCachePromise;
};

/**
 * Horizontal scrolling rail of products for a single category on the home page.
 * Shows a tiny circular category logo next to the title for visual rhythm.
 */
export default function CategoryRail({ category, items, limit = 6 }) {
    const [catLogo, setCatLogo] = useState(null);

    useEffect(() => {
        loadCats().then((cats) => {
            const c = (cats || []).find((x) => x.name === category);
            if (c?.image_url) setCatLogo(c.image_url);
        });
    }, [category]);

    const slice = items.slice(0, limit);
    if (slice.length === 0) return null;
    const total = items.length;
    const categoryUrl = `/c/${encodeURIComponent(category)}`;

    // Fallback to first product photo if no curated logo
    const fallback = items.find((i) => (i.photo_urls && i.photo_urls[0]) || i.photo_url);
    const fallbackUrl = fallback?.photo_urls?.[0] || fallback?.photo_url;
    const logo = catLogo || fallbackUrl;

    return (
        <section className="mb-9" data-testid={`category-rail-${category}`}>
            <div className="flex items-end justify-between mb-3">
                <Link to={categoryUrl} className="flex items-center gap-2.5 group" data-testid={`rail-title-${category}`}>
                    <div className="w-9 sm:w-10 aspect-square rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-ivory-soft to-ivory-soft ring-2 ring-rosegold/20 group-hover:ring-rosegold transition-all">
                        <img
                            src={imgSrc(logo)}
                            alt={category}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.target.src = "/default-item.svg"; }}
                        />
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl text-stone-900 group-hover:text-rosegold transition-colors">{category}</h2>
                </Link>
                <Link
                    to={categoryUrl}
                    data-testid={`view-all-${category}`}
                    className="inline-flex items-center gap-1 text-sm text-rosegold font-semibold hover:underline"
                >
                    View all <ChevronRight size={14} />
                </Link>
            </div>
            <div
                className="flex gap-3 md:gap-4 overflow-x-auto no-scrollbar -mx-4 sm:-mx-6 px-4 sm:px-6 py-2"
                data-testid={`category-rail-scroll-${category}`}
            >
                {slice.map((i) => (
                    <div key={i.id} className="flex-shrink-0 w-[44%] sm:w-[28%] md:w-[22%] lg:w-[200px]">
                        <ProductCard item={i} />
                    </div>
                ))}
                {total > limit && (
                    <Link
                        to={categoryUrl}
                        data-testid={`rail-tile-more-${category}`}
                        className="flex-shrink-0 w-[44%] sm:w-[28%] md:w-[22%] lg:w-[200px] aspect-[3/4] rounded-xl border-2 border-dashed border-border bg-ivory-soft/40 hover:bg-ivory-soft flex flex-col items-center justify-center text-rosegold transition-colors px-3 text-center"
                    >
                        <span className="font-heading text-xl sm:text-2xl font-bold leading-none">+{total - limit}</span>
                        <span className="text-[11px] sm:text-xs font-semibold mt-1.5 leading-tight">more in {category}</span>
                        <span className="text-[10px] mt-1 inline-flex items-center gap-0.5">View all <ChevronRight size={11} /></span>
                    </Link>
                )}
            </div>
        </section>
    );
}
